/*
 * The imperative API a host page drives the editor with.
 *
 * The host owns the accounts and the project storage, so it needs to move
 * projects in and out of the editor without going through React:
 *
 *   window.scratch.loadProject('/api/projects/42/content');
 *   window.scratch.getProjectFile(file => upload(file));
 *   window.scratch.getProjectCover(cover => uploadThumbnail(cover.blob));
 *
 * Everything is callback-based like the rest of the VM's API, and every method
 * also returns a Promise so hosts written with async/await stay readable.
 * Calls made before the editor has finished booting reject with a clear message
 * instead of throwing a null-pointer deep inside the VM.
 *
 * The editor passes in its own action creators rather than a raw dispatch, so
 * this module never has to know the shape of the store.
 */

let vm = null;
let actions = {};

const NOT_READY = 'the editor has not finished loading yet';

const requireVm = () => {
    if (!vm) throw new Error(`window.scratch: ${NOT_READY}`);
    return vm;
};

// Callbacks are optional everywhere, so settle whichever style the host used.
const settle = (callback, resolve, reject, error, value) => {
    if (error) {
        if (typeof callback === 'function') callback(null, error);
        reject(error);
        return;
    }
    if (typeof callback === 'function') callback(value);
    resolve(value);
};

const toPromise = (callback, work) => new Promise((resolve, reject) => {
    let result;
    try {
        result = work();
    } catch (error) {
        settle(callback, resolve, reject, error);
        return;
    }
    if (result && typeof result.then === 'function') {
        result.then(
            value => settle(callback, resolve, reject, null, value),
            error => settle(callback, resolve, reject, error)
        );
        return;
    }
    settle(callback, resolve, reject, null, result);
});

/*
 * Loads a project from a URL the host serves. The response is handed to the VM
 * as an ArrayBuffer: given a Blob the VM treats the bytes as text and rejects a
 * perfectly valid .sb3 archive, which is how a saved project once came back as
 * a blank canvas.
 */
const loadProject = (url, callback) => toPromise(callback, () => {
    requireVm();
    if (!url) throw new Error('window.scratch.loadProject: a url is required');
    return fetch(url, {credentials: 'include'})
        .then(response => {
            if (!response.ok) {
                throw new Error(`window.scratch.loadProject: ${response.status} ${response.statusText}`);
            }
            return response.arrayBuffer();
        })
        .then(buffer => vm.loadProject(buffer))
        .then(() => url);
});

// The current project as an .sb3 Blob, ready to be uploaded by the host.
const getProjectFile = callback => toPromise(callback, () => requireVm().saveProjectSb3());

/*
 * A PNG of the stage, for the thumbnail in the host's project list. The renderer
 * draws on its own canvas, so this is a straight copy rather than a re-render.
 */
const getProjectCover = callback => toPromise(callback, () => {
    const renderer = requireVm().renderer;
    const canvas = (renderer && renderer.canvas) || document.querySelector('canvas');
    if (!canvas) throw new Error('window.scratch.getProjectCover: the stage is not ready');
    if (typeof canvas.toBlob !== 'function') {
        return Promise.resolve({blob: null, dataUrl: canvas.toDataURL('image/png')});
    }
    return new Promise((resolve, reject) => {
        canvas.toBlob(blob => {
            if (!blob) {
                reject(new Error('window.scratch.getProjectCover: the stage could not be captured'));
                return;
            }
            resolve({blob});
        }, 'image/png');
    });
});

const getProjectName = () => (typeof actions.getProjectTitle === 'function' ?
    actions.getProjectTitle() : '');

const setProjectName = name => {
    if (typeof actions.setProjectTitle === 'function') {
        actions.setProjectTitle(String(name === null || typeof name === 'undefined' ? '' : name));
    }
    return getProjectName();
};

const setPlayerOnly = isPlayerOnly => {
    if (typeof actions.setPlayer === 'function') actions.setPlayer(isPlayerOnly !== false);
};

const setFullScreen = isFullScreen => {
    if (typeof actions.setFullScreen === 'function') actions.setFullScreen(isFullScreen !== false);
};

const greenFlag = () => requireVm().greenFlag();

const stopAll = () => requireVm().stopAll();

const getVm = () => vm;

const install = options => {
    const settings = options || {};
    vm = settings.vm || null;
    actions = {
        getProjectTitle: settings.getProjectTitle,
        setProjectTitle: settings.setProjectTitle,
        setPlayer: settings.setPlayer,
        setFullScreen: settings.setFullScreen
    };
    if (typeof window === 'undefined') return null;
    window.scratch = {
        loadProject,
        getProjectFile,
        getProjectCover,
        getProjectName,
        setProjectName,
        setPlayerOnly,
        setFullScreen,
        greenFlag,
        stopAll,
        getVm
    };
    return window.scratch;
};

// Lets the editor drop its references when it unmounts, so a destroyed VM is not
// left reachable from the host page.
const uninstall = () => {
    vm = null;
    actions = {};
    if (typeof window !== 'undefined' && window.scratch) delete window.scratch;
};

export {
    getProjectCover,
    getProjectFile,
    getProjectName,
    getVm,
    greenFlag,
    install,
    loadProject,
    setFullScreen,
    setPlayerOnly,
    setProjectName,
    stopAll,
    uninstall
};
